import { css, styled } from 'styled-components';

import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS, GRADIENTS, RETAILER_COLORS, SCREEN_SIZE } from '@components/web/src/styles';

export const ProductCardContainer = styled(Flexbox)<{
  $isProductFeedbackFinished: boolean;
  $isCardClickDisabled: boolean;
  $isResponsive: boolean;
}>`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: ${({ $isProductFeedbackFinished }) =>
    $isProductFeedbackFinished ? GRADIENTS.characterGradient : COLORS.base.white};
  pointer-events: ${({ $isCardClickDisabled }) => ($isCardClickDisabled ? 'none' : 'auto')};
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);

  ${({ theme, $isResponsive }) =>
    theme?.isKioskRootElementType
      ? css`
          width: ${$isResponsive ? '100%' : '328px'};
        `
      : css`
          width: ${$isResponsive ? '100%' : '210px'};

          @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
            width: ${$isResponsive ? '100%' : '328px'};
          }
        `}

  ${({ $isCardClickDisabled }) =>
    $isCardClickDisabled &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        backdrop-filter: blur(2.5px);
        background: linear-gradient(180deg, #e4e4e4 0%, rgba(228, 228, 228, 0) 100%);
      }
    `}
`;

export const ProductImageWrapper = styled(Flexbox)<{
  $productBackgroundImage?: string;
}>`
  position: relative;
  width: 100%;
  height: 108px;
  padding: 8px;
  background: ${({ $productBackgroundImage, theme }) => {
    if ($productBackgroundImage) return `center/cover no-repeat url(${$productBackgroundImage})`;
    if (theme?.isKioskRootElementType) return GRADIENTS.warmGradientReversed();
    return '';
  }};
  border-top: 16px;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    height: 210px;
    padding: 5px;
  }
`;

export const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    height: 90%;
  }
`;

export const PromoLabel = styled.div`
  position: absolute;
  z-index: 1;
  top: 10px;
  left: -30px;
  width: 100px;
  padding: 3px 0;
  background-color: ${COLORS.base.red};
  transform: rotate(-45deg);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    top: 14px;
    left: -25px;
  }
`;

export const ProductTags = styled.div`
  display: none;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    display: block;
    position: absolute;
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: flex-end;
    height: 100%;
    padding: 16px;
  }
`;

export const CTAContainer = styled(Flexbox)`
  position: absolute;
  right: 0;
  top: 0;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    right: 8px;
    top: 8px;
  }
`;

export const ProductContentWrapper = styled(Flexbox)`
  flex: 1;
  gap: 8px;
  padding: 0 8px 8px;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    padding: 16px;
  }
`;

export const ProducerName = styled(Text)`
  min-height: 18px;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    min-height: 24px;
  }
`;

export const ProductName = styled(Text)`
  ${({ theme }) =>
    theme?.isKioskRootElementType
      ? css`
          min-height: 56px;
        `
      : css`
          min-height: 48px;

          @container rootContainer (min-width: ${SCREEN_SIZE.DESKTOP_WIDTH}) {
            min-height: 64px;
          }
        `}
`;

export const ProductDescription = styled(Text)`
  min-height: 56px;

  @container rootContainer (min-width: ${SCREEN_SIZE.DESKTOP_WIDTH}) {
    min-height: 72px;
  }
`;

export const CharacterTitle = styled(Text)`
  min-height: 28px;

  @container rootContainer (min-width: ${SCREEN_SIZE.DESKTOP_WIDTH}) {
    min-height: 36px;
  }

  strong {
    color: ${RETAILER_COLORS.product['500'] ?? 'inherit'};
  }
`;

export const ProductPriceFormatWrapper = styled(Flexbox)`
  min-height: 24px;

  @container rootContainer (min-width: ${SCREEN_SIZE.DESKTOP_WIDTH}) {
    min-height: 32px;
  }

  & > div {
    margin-left: auto;
  }
`;

export const ProductFormat = styled(Text)`
  display: none;
  flex: 1;
  color: ${COLORS.base.black};

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    display: block;
  }
`;

export const FeedbackStatus = styled(Flexbox)`
  height: 30px;
  gap: 4px;

  @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
    height: 32px;
  }
`;
